import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { getUserSessionsHistory } from '../../../data/userSessions'
import { UserFriendlyError } from '../../../model/Error'
import { SessionHistoryEvent } from '../../../model/UserSession'
import { singleItemPageLimit } from '../../../constants'
import { userSessionsKeys } from './userSessions.keys'

// Aligné sur la taille de page du composant `List` : son waypoint d'infinite-scroll
// est positionné à `items.length - singleItemPageLimit / 2`. Une taille de page
// différente placerait le sentinel en haut de la première page, déclenchant le
// chargement de toutes les pages dès l'ouverture du panneau.
export const HISTORY_PAGE_SIZE = singleItemPageLimit

interface UseGetUserSessionsHistoryParams {
  enabled?: boolean
}

export const useGetUserSessionsHistory = ({
  enabled = true,
}: UseGetUserSessionsHistoryParams = {}) => {
  const queryKey = useMemo(() => userSessionsKeys.historyList(), [])

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const { data, ok } = await getUserSessionsHistory(
        { first: pageParam, max: HISTORY_PAGE_SIZE },
        signal,
      )

      if (!data || !ok) {
        throw new UserFriendlyError(
          'error',
          "Impossible de charger l'historique des connexions",
          'Une erreur est survenue. Vérifiez votre connexion et réessayez.',
        )
      }

      return data
    },
    initialPageParam: 0,
    // Pas de total renvoyé par l'API : on estime qu'il reste des pages tant que
    // la dernière page est "pleine".
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < HISTORY_PAGE_SIZE
        ? undefined
        : allPages.reduce((total, page) => total + page.length, 0),
    enabled,
  })

  const events: SessionHistoryEvent[] = useMemo(() => query.data?.pages.flat() ?? [], [query.data])

  return { query, events }
}
